import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, Loader2, Check, X, Clock, Sparkles, FileText, Brain, Key } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const moduleLabels = { passions: 'Passions', talents: 'Talents', utility: 'Utilité', monetization: 'Monétisation' };
const moduleColors = { passions: '#FF6B6B', talents: '#FFE66D', utility: '#4ECDC4', monetization: '#1A535C' };

const StatusBadge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${ok ? 'bg-green-50 text-green-700' : 'bg-stone-50 text-stone-400'}`}>
    {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
    {label}
  </span>
);

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Accès réservé à l\'administrateur');
        navigate('/dashboard');
      }
      console.warn('Admin users:', e.message);
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
    </div>
  );

  const stats = {
    total: users.length,
    withMiniProfile: users.filter(u => u.has_mini_profile).length,
    withFullProfile: users.filter(u => u.has_full_profile).length,
    withPurchases: users.filter(u => u.purchases?.length > 0).length,
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="glass-header sticky top-0 z-50 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="text-stone-600 hover:text-stone-900" data-testid="admin-users-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#FF6B6B]" />
            <span className="font-serif font-semibold text-stone-800">Suivi utilisateurs</span>
          </div>
          <button onClick={() => navigate('/admin/promo')} className="text-stone-400 hover:text-stone-600" data-testid="go-promo-link">
            <Key className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Inscrits', value: stats.total, color: 'stone' },
            { label: 'Mini-profil', value: stats.withMiniProfile, color: 'blue' },
            { label: 'Profil complet', value: stats.withFullProfile, color: 'green' },
            { label: 'Acheteurs', value: stats.withPurchases, color: 'amber' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl p-3 text-center border border-stone-100">
              <p className="text-xl font-serif font-bold text-stone-900">{s.value}</p>
              <p className="text-[10px] text-stone-400 uppercase tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>

        {/* User list */}
        <div className="space-y-3">
          {users.map(u => {
            const totalQuestions = Object.values(u.response_counts || {}).reduce((a, b) => a + b, 0);
            const modulesCompleted = u.modules_completed?.length || 0;
            const progressPct = u.total_progress || 0;

            return (
              <div key={u.id} className="bg-white rounded-xl p-4 border border-stone-100 shadow-sm" data-testid={`user-card-${u.id}`}>
                {/* User header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-medium text-stone-900 text-sm">{u.name || 'Sans nom'}</p>
                    <p className="text-xs text-stone-400">{u.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-stone-400">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : ''}
                    </p>
                    <span className="text-xs text-stone-300">{u.language?.toUpperCase()}</span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-stone-500">Progression</span>
                    <span className="text-xs font-medium text-stone-700">{progressPct}%</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4]" style={{ width: `${progressPct}%` }} />
                  </div>
                </div>

                {/* Module breakdown */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {['passions', 'talents', 'utility', 'monetization'].map(mod => {
                    const count = u.response_counts?.[mod] || 0;
                    const completed = u.modules_completed?.includes(mod);
                    return (
                      <div key={mod} className="text-center">
                        <div className={`text-xs font-medium mb-1 ${completed ? '' : 'text-stone-300'}`} style={completed ? { color: moduleColors[mod] } : {}}>
                          {moduleLabels[mod]}
                        </div>
                        <div className={`text-sm font-bold ${completed ? 'text-stone-900' : 'text-stone-300'}`}>
                          {count}q
                        </div>
                        {completed && <Check className="w-3 h-3 mx-auto text-green-500" />}
                      </div>
                    );
                  })}
                </div>

                {/* Status badges */}
                <div className="flex flex-wrap gap-1.5">
                  <StatusBadge ok={u.has_mini_profile} label="Mini-profil" />
                  <StatusBadge ok={u.has_full_profile} label="Profil IA" />
                  <StatusBadge ok={u.purchases?.includes('diagnostic')} label="Diagnostic" />
                  <StatusBadge ok={u.purchases?.includes('plan_90_days')} label="Plan 90j" />
                  <StatusBadge ok={u.has_plan_90} label="Plan généré" />
                  <span className="text-xs text-stone-300 ml-auto">{totalQuestions} réponses</span>
                </div>
              </div>
            );
          })}
        </div>

        {users.length === 0 && (
          <div className="text-center py-12 text-stone-400">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucun utilisateur inscrit</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminUsers;
